import * as RadixToggle from '@radix-ui/react-toggle';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef, useId, useState } from 'react';

import { cn } from '~/chords/lib/cn';
import { type VariantProps, variants } from '~/chords/lib/variants';

const wrapperVariants = variants({
  base: 'relative flex items-center justify-start gap-md',
  variants: {
    variant: {
      formField: 'h-[3rem]',
    },
  },
});

export type ToggleProps = RadixToggle.ToggleProps &
  VariantProps<typeof wrapperVariants> & {
    // Shown when the Toggle is active/pressed/checked
    activeLabel?: ReactNode;
    // Shown when the Toggle is not active/pressed/checked
    inactiveLabel?: ReactNode;
  };

export const Toggle = forwardRef(
  (
    {
      className,
      activeLabel,
      inactiveLabel,
      pressed,
      onPressedChange,
      defaultPressed,
      id: defaultId,
      variant,
      ...props
    }: ToggleProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const id = useId();

    // Used only to set the correct label text in uncontrolled mode, it's not passed to RadixToggle
    // and does not affect the toggle's functionality. When `pressed` is provided, the label follows
    // it directly so it can't drift from the switch (e.g. when a form resets the value).
    const [uncontrolledPressed, setUncontrolledPressed] = useState(
      defaultPressed ?? false,
    );
    const isPressed = pressed ?? uncontrolledPressed;

    const handlePressedChange = (newPressed: boolean) => {
      setUncontrolledPressed(newPressed);
      onPressedChange?.(newPressed);
    };

    return (
      <div className={cn(wrapperVariants({ variant }))}>
        <RadixToggle.Toggle
          id={defaultId ?? id}
          className={cn(
            'relative',
            'bg-emphasis-default',
            'rounded-full',
            'w-[3rem]',
            'h-lg',
            'p-xs',
            'transition-colors',
            'after:content-[""]',
            'after:rounded-full',
            'after:bg-default-inverse',
            'after:w-md',
            'after:h-md',
            'after:absolute',
            'after:inset-xs',
            'after:transition-transform',
            'aria-pressed:enabled:bg-primary-default',
            'aria-pressed:after:translate-x-lg',
            'disabled:bg-level-05',
            'disabled:cursor-not-allowed',
            'disabled:after:bg-level-01',
            className,
          )}
          ref={ref}
          pressed={pressed}
          onPressedChange={handlePressedChange}
          defaultPressed={defaultPressed}
          {...props}
        />

        {activeLabel || inactiveLabel ? (
          <label
            htmlFor={defaultId ?? id}
            className="flex w-auto text-body-sm text-white"
          >
            {isPressed ? activeLabel : inactiveLabel}
          </label>
        ) : null}
      </div>
    );
  },
);

Toggle.displayName = 'Toggle';
